import React from 'react';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { UniversitiesCard } from 'modules/blok-test';
import { getRegions, globalConst, isEmptyArr } from 'modules/global';
import {
  getAllUniversities,
  LeftSideFirstSection,
  LeftSideSecondSection,
  RegionFilterDropdown,
  // RightSideCard,
  TypeFilterDropdown,
  universitiesConst,
  useAllUniversities
} from 'modules/universities';

import { CustomPagination, EmptyData, NextHead, SearchInput, Visible } from 'components';

const Index = () => {
  const t = useTranslations();
  const { query } = useRouter();

  const { universities, isLoading, refetch } = useAllUniversities({
    search: (query?.search as string) || undefined,
    page: query.page,
    addressId: query.address_id,
    type: query.type
  });

  return (
    <>
      <NextHead title={t('meta.university.title')} description={t('meta.university.description')} />

      <div className="mx-auto mt-3 min-h-screen max-w-screen px-4 md:mt-10">
        <h1 className="text-left text-center text-xl font-bold md:mb-6 md:text-2xl">{t('Oliygohlar_title')}</h1>

        <div className="mt-3 grid grid-cols-1 gap-4 md:mt-5 md:grid-cols-4">
          {/* CHAP TOMON */}
          <div className=" hidden self-start md:sticky md:top-4 md:block">
            <div className="space-y-3">
              <LeftSideFirstSection />
              <LeftSideSecondSection />
            </div>
          </div>

          {/* O"NG TOMON */}
          <div className="sm:col-span-3">
            <div className="flex flex-col gap-3 rounded-xl bg-transparent sm:flex-row sm:items-center sm:justify-between sm:bg-white sm:px-4 sm:py-2 md:py-3 md:shadow-sm">
              <div className="hidden items-center gap-2 text-lg font-medium md:flex">{t('Oliygohlar_title')}</div>
              <SearchInput refetch={refetch} loading={isLoading} />
              <div className="flex gap-2 sm:items-center sm:gap-2 md:hidden">
                <TypeFilterDropdown />
                <RegionFilterDropdown />
              </div>
            </div>

            <Visible
              if={isLoading}
              else={
                <Visible
                  if={isEmptyArr(universities?.data)}
                  else={
                    <div className="xs:grid-cols-2 mt-3 grid grid-cols-1 gap-4 self-start sm:grid-cols-2 md:col-span-3 md:grid-cols-2 lg:grid-cols-3">
                      {universities?.data?.map((uni, index) => (
                        <UniversitiesCard index={index} key={uni.id} data={uni} />
                      ))}
                    </div>
                  }
                >
                  <EmptyData title={t('malumot_topilmadi')} />
                </Visible>
              }
            >
              <div className="mt-4 grid grid-cols-1 gap-4 self-start sm:grid-cols-2 md:col-span-3 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 10 }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Skeleton.Button key={index} active className="!h-80 !w-full !rounded-xl" />
                ))}
              </div>
            </Visible>
            {universities?.data && (
              <div className="mt-5">
                <CustomPagination pageSize={12} total={universities.pagination?.total_items} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Index;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const queryClient = new QueryClient();

  await Promise.allSettled([
    queryClient.prefetchQuery({
      queryKey: [universitiesConst.queryKeys.ALL_UNIVERSITIES, { size: 12 }],
      queryFn: getAllUniversities({})
    }),
    queryClient.prefetchQuery({
      queryKey: [globalConst.queryKeys.GET_ALL_REGION],
      queryFn: getRegions
    })
  ]);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      messages: (await import(`../../messages/${locale}.json`)).default
    },
    revalidate: 604800
  };
};
